import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { renderMarkdown } from '../utils/markdown';
import { eventDateFromFilename } from '../utils/filenameDate';
import { watchUploadStatus, UploadWatchState } from '../utils/uploadStatusStream';
import { uploadWithProgress } from '../utils/uploadWithProgress';
import UploadCalendar, { CalEvent } from '../components/UploadCalendar';
// 행사 시작·시간·종료 양방향 계산 — 영상 상세/업로드 모달과 **같은 규칙**을 쓴다.
import {
  durationFromEnd, endFromDuration, humanizeMinutes, isRangeInvalid, onStartChanged, snapOnStartBlur,
} from '../utils/eventDuration';

// 비로그인(익명) 영상 업로드 페이지 — mocombinator.com/@{handle}.
// 흐름: 공개 프로필(배경 + 안내) → 업로드 코드 입력(→ 스코프 토큰) → 업로드(오버레이 위치
// 지정) → "등록 대기 완료". 로그인 불필요(세션 무관, 스코프 토큰만 사용). 백엔드 API 는
// /public-upload/@:handle 프리픽스. dangerouslySetInnerHTML 미사용(모든 값은 JSX 텍스트).

// 오버레이 위치 라벨(1~5) — FileDetailModal 과 동일. 가이드 이미지 /static/overlay-pos/pos{n}.png.
const OVERLAY_POS_LABELS: Record<number, string> = { 1: '현재', 2: '좌상단', 3: '좌하단', 4: '우하단', 5: '우상단' };

interface Profile {
  handle: string | null;
  name: string | null;
  profileBackground: string | null;
  notice: string | null;
}

type Phase = 'loading' | 'notfound' | 'code' | 'upload' | 'done';

// 선택한 행사 날짜(YYYY-MM-DD)로 "행사까지 남은 날짜" 미리보기(오버레이 카운트다운과
// 동일 개념). 날짜 기준(시간 무시)으로 오늘과의 일수 차이를 계산.
function countdownPreview(eventDate: string): { text: string; past: boolean } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(eventDate || '');
  if (!m) return null;
  const d1 = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((d1.getTime() - d0.getTime()) / 86400000);
  if (days > 0) return { text: `행사까지 D-${days} · ${days}일 남음`, past: false };
  if (days === 0) return { text: '오늘 행사 · D-DAY', past: false };
  return { text: `지난 행사 · ${-days}일 전`, past: true };
}

// datetime-local 값의 날짜 부분("YYYY-MM-DD")을 주어진 날짜로 교체(시각 부분은 유지, 없으면 00:00).
// 달력 클릭이 datetime-local 의 날짜만 채우는 데 쓴다.
function withDatePart(startLocal: string, date: string): string {
  const time = startLocal.length >= 16 ? startLocal.slice(11, 16) : '00:00';
  return `${date}T${time}`;
}


// localStorage 안전 접근(프라이빗 모드·차단 시 throw 방어). 코드/이메일 이전 입력값 기억용.
// SPA 는 preventDefault 제출이라 브라우저 네이티브 폼-히스토리 저장이 코드 필드엔 잘 안 걸려,
// 앱 레벨 localStorage 로 확실히 기억한다(핸들별 코드 키 + 공용 이메일 키).
const lsGet = (k: string): string => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const lsSet = (k: string, v: string): void => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };
const codeKey = (handle: string | null): string => `moc:uploadCode:${handle || ''}`;
const EMAIL_KEY = 'moc:uploadEmail';


// 첨부 자료 크기 표시(격리 /attachments 섹션용).
const fmtBytes = (b: number): string =>
  b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
// 공개 첨부 목록 그룹(영상 제목별) / 방금 올린 첨부.
type PubGroup = { fileHash: string; videoTitle: string; attachments: { id: number; filename: string; byteSize: number }[] };
type MyAttachment = { id: number; filename: string; isPublic: boolean; byteSize: number };
// 영상 첨부 — 재생/다운로드. 50MB 상한이라 blob 처리(스트리밍 불필요).
const isVideoName = (f: string): boolean => /\.(mp4|webm|mov|m4v)$/i.test(f);
const videoMime = (f: string): string =>
  /\.webm$/i.test(f) ? 'video/webm' : /\.mov$/i.test(f) ? 'video/quicktime' : /\.m4v$/i.test(f) ? 'video/x-m4v' : 'video/mp4';
// 영상 썸네일 — 공개 자료는 무인증 <img src>(민감 통로 아님). 없거나 실패하면 숨김.
const VideoThumb: React.FC<{ id: number }> = ({ id }) => {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return <img src={`/attachments/attachment/${id}/thumbnail`} onError={() => setOk(false)} alt=""
    className="w-16 h-10 object-cover rounded bg-black shrink-0" />;
};

const PublicUploadPage: React.FC = () => {
  // URL 세그먼트는 '@handle' 전체(라우트가 단일 세그먼트로 받음). '@' 접두사가 있어야
  // 공개 업로드 페이지 — 없으면 not-found(다른 단일 세그먼트 경로를 가로채지 않도록).
  const { handle: rawHandle } = useParams<{ handle: string }>();
  const handle = rawHandle && rawHandle.startsWith('@') ? rawHandle.slice(1) : null;
  const [phase, setPhase] = useState<Phase>('loading');
  const [profile, setProfile] = useState<Profile | null>(null);

  // 이전 입력값 기억: 마운트 시 localStorage 에서 코드(핸들별)·이메일 프리필.
  const [code, setCode] = useState(() => lsGet(codeKey(handle)));
  const [email, setEmail] = useState(() => lsGet(EMAIL_KEY));
  const [token, setToken] = useState('');
  // 업로드 응답 이후 "게재 준비" 상태(NDJSON 스트림). phase==='done' 화면이 이 값을 보여준다.
  const [watch, setWatch] = useState<UploadWatchState | null>(null);
  const watcherRef = useRef<{ stop: () => void } | null>(null);
  useEffect(() => () => watcherRef.current?.stop(), []);
  const [codeError, setCodeError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [calendarEvents, setCalendarEvents] = useState<CalEvent[]>([]);
  // 날짜/시간 분리 — 행사 시간은 제각각이라 달력 클릭은 날짜만 채우고 시간은 사용자 입력.
  // 시작·종료 모두 datetime-local("YYYY-MM-DDTHH:mm")로 통일. 달력 클릭은 시작의 날짜 부분만 채운다.
  const [eventStart, setEventStart] = useState(''); // 시작 일시(선택)
  const [eventEnd, setEventEnd] = useState(''); // 종료(만료·선택).
  // 행사 시간(분, 문자열 — 타이핑 중 빈값 허용). 시작·종료와 **양방향**으로 물린다:
  //   행사 시간 입력 → 종료 = 시작 + 행사 시간 / 종료 입력 → 행사 시간 = 종료 − 시작.
  // 서버로는 보내지 않는다(입력 보조). 저장되는 값은 시작·종료뿐.
  const [durationMin, setDurationMin] = useState('');
  const [overlayPos, setOverlayPos] = useState(5); // 기본 우상단(2026-09-09)

  // ── 첨부 자료(격리: /attachments) — 코드 입력 후 공개 목록(영상 제목별) + 올린 영상에 자료 첨부 ──
  const [pubGroups, setPubGroups] = useState<PubGroup[]>([]);
  const [pubAccount, setPubAccount] = useState<{ id: number; filename: string; byteSize: number }[]>([]);
  const [myAttachments, setMyAttachments] = useState<MyAttachment[]>([]);
  const [attachIsPublic, setAttachIsPublic] = useState(true);
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachErr, setAttachErr] = useState('');
  const attachInputRef = useRef<HTMLInputElement | null>(null);
  const [attachFiles, setAttachFiles] = useState<File[]>([]); // 선택만 하고 '올리기' 버튼으로 전송
  // 계정 자료(영상 무관, "자료 보내기") — 코드 입력 후 별도 업로드.
  const [myAccount, setMyAccount] = useState<MyAttachment[]>([]);
  const [acctIsPublic, setAcctIsPublic] = useState(true);
  const [acctUploading, setAcctUploading] = useState(false);
  const acctInputRef = useRef<HTMLInputElement | null>(null);
  const [acctFiles, setAcctFiles] = useState<File[]>([]); // 선택만 하고 '올리기' 버튼으로 전송
  const dlAnchorRef = useRef<HTMLAnchorElement | null>(null);
  // 공개 다운로드는 토큰을 헤더로 보내야 하므로(URL 금지) fetch→blob→숨은 앵커 클릭.
  const downloadPublic = async (id: number, filename: string) => {
    if (!handle || !token) return;
    try {
      const r = await fetch(`/attachments/public/@${encodeURIComponent(handle)}/attachment/${id}/download`, {
        headers: { 'x-upload-token': token },
      });
      if (!r.ok) { setAttachErr('내려받기에 실패했어요.'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = dlAnchorRef.current;
      if (a) { a.href = url; a.download = filename; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { setAttachErr('내려받기에 실패했어요.'); }
  };
  // 영상 인라인 재생(확인용) — 같은 blob 을 받아 objectURL 로 <video> 에 물린다(스트리밍 아님).
  const [playing, setPlaying] = useState<{ id: number; url: string } | null>(null);
  const playingUrlRef = useRef<string | null>(null);
  useEffect(() => () => { if (playingUrlRef.current) URL.revokeObjectURL(playingUrlRef.current); }, []);
  const playPublic = async (id: number, filename: string) => {
    if (!handle || !token) return;
    if (playing?.id === id) { // 토글 off
      if (playingUrlRef.current) { URL.revokeObjectURL(playingUrlRef.current); playingUrlRef.current = null; }
      setPlaying(null); return;
    }
    setAttachErr('');
    try {
      const r = await fetch(`/attachments/public/@${encodeURIComponent(handle)}/attachment/${id}/download`, {
        headers: { 'x-upload-token': token },
      });
      if (!r.ok) { setAttachErr('재생할 수 없어요.'); return; }
      const blob = new Blob([await r.blob()], { type: videoMime(filename) });
      const url = URL.createObjectURL(blob);
      if (playingUrlRef.current) URL.revokeObjectURL(playingUrlRef.current);
      playingUrlRef.current = url;
      setPlaying({ id, url });
    } catch { setAttachErr('재생할 수 없어요.'); }
  };
  // 공개 자료 한 줄 — 파일명 + (영상이면)재생 + 내려받기, 재생 중이면 <video>.
  const renderPubItem = (a: { id: number; filename: string; byteSize: number }) => (
    <li key={a.id} className="px-2 py-1.5 text-sm">
      <div className="flex items-center gap-2">
        {isVideoName(a.filename) && <VideoThumb id={a.id} />}
        <span className="flex-1 truncate text-gray-700" title={a.filename}>{a.filename}</span>
        <span className="text-xs text-gray-400 shrink-0">{fmtBytes(a.byteSize)}</span>
        {isVideoName(a.filename) && (
          <button type="button" onClick={() => playPublic(a.id, a.filename)}
            className="text-xs text-indigo-600 hover:underline shrink-0">{playing?.id === a.id ? '■ 닫기' : '▶ 재생'}</button>
        )}
        <button type="button" onClick={() => downloadPublic(a.id, a.filename)}
          className="text-xs text-blue-600 hover:underline shrink-0">⬇ 내려받기</button>
      </div>
      {playing?.id === a.id && (
        <video src={playing.url} controls autoPlay className="w-full mt-2 rounded bg-black"
          onError={() => setAttachErr('이 브라우저에서 미리보기를 지원하지 않는 형식입니다 — 내려받아 확인하세요.')} />
      )}
    </li>
  );
  const loadPublicAttachments = () => {
    if (!handle || !token) return;
    fetch(`/attachments/public/@${encodeURIComponent(handle)}`, { headers: { 'x-upload-token': token } })
      .then((r) => (r.ok ? r.json() : { data: { groups: [], account: [] } }))
      .then((d) => { setPubGroups(d.data?.groups ?? []); setPubAccount(d.data?.account ?? []); })
      .catch(() => { setPubGroups([]); setPubAccount([]); });
  };
  useEffect(() => { if ((phase === 'upload' || phase === 'done') && token) loadPublicAttachments(); }, [phase, token]);
  const uploadAttachments = async () => {
    if (attachFiles.length === 0 || !handle || !token) return;
    setAttachErr(''); setAttachUploading(true);
    try {
      for (const f of attachFiles) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('is_public', attachIsPublic ? 'true' : 'false');
        const r = await fetch(`/attachments/public/@${encodeURIComponent(handle)}/upload`, {
          method: 'POST', headers: { 'x-upload-token': token }, body: fd,
        });
        const d = await r.json().catch(() => ({}));
        if (r.ok && d.success) {
          setMyAttachments((prev) => [...prev, { id: d.data.id, filename: d.data.filename, isPublic: d.data.isPublic, byteSize: d.data.byteSize }]);
        } else {
          setAttachErr(d.error || '첨부 업로드에 실패했어요.');
        }
      }
      loadPublicAttachments();
    } finally {
      setAttachUploading(false);
      setAttachFiles([]);
      if (attachInputRef.current) attachInputRef.current.value = '';
    }
  };
  // 영상 없이 운영처 계정으로 자료 보내기(코드 입력 후).
  const uploadAccountMaterials = async () => {
    if (acctFiles.length === 0 || !handle || !token) return;
    setAttachErr(''); setAcctUploading(true);
    try {
      for (const f of acctFiles) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('is_public', acctIsPublic ? 'true' : 'false');
        const r = await fetch(`/attachments/public/@${encodeURIComponent(handle)}/account-upload`, {
          method: 'POST', headers: { 'x-upload-token': token }, body: fd,
        });
        const d = await r.json().catch(() => ({}));
        if (r.ok && d.success) {
          setMyAccount((prev) => [...prev, { id: d.data.id, filename: d.data.filename, isPublic: d.data.isPublic, byteSize: d.data.byteSize }]);
        } else {
          setAttachErr(d.error || '자료 전송에 실패했어요.');
        }
      }
      loadPublicAttachments();
    } finally {
      setAcctUploading(false);
      setAcctFiles([]);
      if (acctInputRef.current) acctInputRef.current.value = '';
    }
  };
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false); // 바이트 업로드 완료 후 서버 처리(썸네일/등록) 대기
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const [selectedIsImage, setSelectedIsImage] = useState(false);
  // 파일 입력을 숨기고 버튼으로 트리거 — 모바일에서 accept/capture 로 카메라(영상·사진 촬영) 직접 열기.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState('');

  // 파일 입력의 accept/capture 설정 후 트리거. capture=true → 모바일 카메라 직접(데스크톱은 무시=일반 선택).
  const pickFile = (accept: string, capture: boolean) => {
    const input = fileInputRef.current;
    if (!input) return;
    input.accept = accept;
    if (capture) input.setAttribute('capture', 'environment');
    else input.removeAttribute('capture');
    input.value = ''; // 같은 파일 재선택 허용
    input.click();
  };

  // 시작 일시 변경 → **행사 시간을 유지한 채** 종료를 옮긴다(시작만 미루는 게 흔한 편집이다).
  // 행사 시간이 비어 있으면 기본 60분 — 종전 "종료 = 시작 + 1시간" 규칙과 같은 결과다.
  // 빈값으로 지우면 종료·행사 시간도 함께 지운다.
  //
  // ⚠️ effect 가 아니라 함수로 둔다. 시작을 바꾸는 경로가 둘(직접 입력·달력 클릭)이라
  //    양쪽이 같은 규칙을 타야 하고, [eventStart] effect 로 두면 행사 시간을 지우는
  //    입력과 서로 덮어써 값이 튄다.
  const applyEventStart = (next: string) => {
    const r = onStartChanged(next, durationMin);
    setEventStart(r.start); setEventEnd(r.end); setDurationMin(r.durationMin);
  };

  const onChangeDuration = (v: string) => {
    setDurationMin(v);
    const end = endFromDuration(eventStart, v);
    if (end !== null) setEventEnd(end);
  };

  const onChangeEventEnd = (v: string) => {
    setEventEnd(v);
    setDurationMin(durationFromEnd(eventStart, v));
  };

  const onBlurEventStart = () => {
    const snap = snapOnStartBlur(eventStart, eventEnd);
    if (snap) { setEventEnd(snap.end); setDurationMin(snap.durationMin); }
  };

  const durationInvalid = isRangeInvalid(eventStart, eventEnd);

  // 1) 공개 프로필 로드. '@' 접두사 없는 경로면 즉시 not-found(가로채기 방지).
  useEffect(() => {
    if (!handle) {
      setPhase('notfound');
      return;
    }
    let alive = true;
    fetch(`/public-upload/@${encodeURIComponent(handle)}`)
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!alive) return;
        if (ok && j?.success) {
          setProfile(j.data);
          setPhase('code');
        } else {
          setPhase('notfound');
        }
      })
      .catch(() => alive && setPhase('notfound'));
    return () => {
      alive = false;
    };
  }, [handle]);

  // 업로드 단계 진입 시 승인 현황 달력 로드(스코프 토큰 헤더). 실패는 무시(부가 정보).
  useEffect(() => {
    if (phase !== 'upload' || !token || !handle) return;
    let alive = true;
    fetch(`/public-upload/@${encodeURIComponent(handle)}/calendar`, { headers: { 'x-upload-token': token } })
      .then((r) => r.json())
      .then((j) => { if (alive && j?.success) setCalendarEvents(j.data?.events ?? []); })
      .catch(() => { /* 부가 정보라 실패 무시 */ });
    return () => { alive = false; };
  }, [phase, token, handle]);

  // 코드로 스코프 토큰 발급(1회용 — pending 행 하나에 대응). 성공 시 token 세팅 + true.
  const requestToken = async (): Promise<{ ok: boolean; error?: string }> => {
    const r = await fetch(`/public-upload/@${encodeURIComponent(handle || '')}/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, email: email.trim() || undefined }),
    });
    const j = await r.json();
    if (r.ok && j?.success) {
      setToken(j.data.token);
      return { ok: true };
    }
    return { ok: false, error: j?.error || '코드를 확인하지 못했어요.' };
  };

  // 2) 코드 검증 → 업로드 폼.
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError('');
    setVerifying(true);
    try {
      const res = await requestToken();
      if (res.ok) {
        // 성공한 코드·이메일을 기억(다음 방문 프리필). 코드는 핸들별.
        lsSet(codeKey(handle), code);
        lsSet(EMAIL_KEY, email.trim());
        setPhase('upload');
      } else setCodeError(res.error!);
    } catch {
      setCodeError('네트워크 오류예요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setVerifying(false);
    }
  };

  // 완료 후 추가 업로드 — 저장된 코드로 새 토큰 재발급(코드 재입력 없이).
  const handleUploadMore = async () => {
    watcherRef.current?.stop();
    watcherRef.current = null;
    setWatch(null);
    formRef.current?.reset();
    setSelectedIsImage(false);
    setSelectedFileName('');
    setEventStart(''); setEventEnd(''); setDurationMin(''); // 제어 입력이라 form.reset() 으로 안 지워짐
    setProgress(0);
    setUploadError('');
    try {
      const res = await requestToken();
      if (res.ok) setPhase('upload');
      else {
        setCodeError(res.error!);
        setPhase('code'); // 코드 만료/한도 초과 → 코드 화면으로.
      }
    } catch {
      setCodeError('네트워크 오류예요. 잠시 후 다시 시도해 주세요.');
      setPhase('code');
    }
  };

  // 3) 업로드(스코프 토큰 헤더 + 진행률).
  const handleUpload = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUploadError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const fileEntry = formData.get('file');
    if (!(fileEntry instanceof File) || fileEntry.size === 0) {
      setUploadError('영상 파일을 선택해 주세요.');
      return;
    }

    // 행사 시작: datetime-local 있으면 로컬 → ISO. 없으면 미전송.
    if (eventStart) {
      formData.set('event_start_at', new Date(eventStart).toISOString());
      // 행사 종료(만료): datetime-local 이라 시작과 다른 날 가능. 값 있으면 로컬 → ISO.
      if (eventEnd) {
        formData.set('expires_at', new Date(eventEnd).toISOString());
      } else {
        formData.delete('expires_at');
      }
    } else {
      formData.delete('event_start_at');
      formData.delete('expires_at');
    }

    // 동적 라벨 빈값 제거.
    const lblRaw = formData.get('event_label');
    if (!(typeof lblRaw === 'string' && lblRaw.trim())) formData.delete('event_label');

    // 오버레이 위치.
    formData.set('overlay_position', String(overlayPos));

    // 이미지 → 영상 변환 파라미터(이미지 선택 시 필수).
    if (selectedIsImage) {
      const durInput = form.elements.namedItem('image_duration_secs') as HTMLInputElement | null;
      const widthInput = form.elements.namedItem('image_width') as HTMLInputElement | null;
      const dur = parseFloat(durInput?.value || '');
      const w = parseInt(widthInput?.value || '', 10);
      if (!Number.isFinite(dur) || dur <= 0 || !Number.isInteger(w) || w <= 0) {
        setUploadError('사진은 보여줄 시간(초)과 너비(px)를 적어 주세요.');
        return;
      }
    }

    setUploading(true);
    setProcessing(false);
    setProgress(0);
    // 바이트 진행률 + "바이트 다 나감"(Chromium onload 지연 우회)은 공용 헬퍼가 처리한다.
    const url = `/public-upload/@${encodeURIComponent(handle || '')}/upload`;
    const { promise } = uploadWithProgress({
      url, formData,
      withCredentials: false,                    // 스코프 토큰은 세션 쿠키가 아닌 전용 헤더 — 세션과 완전 분리.
      headers: { 'x-upload-token': token },
      onProgress: (pct) => setProgress(pct),
      onBytesDone: () => setProcessing(true),
    });
    promise.then(({ status, json }) => {
      setUploading(false);
      setProcessing(false);
      if (status === 200) {
        setPhase('done');
        // 응답 뒤 서버 쪽 "게재 준비"를 스트림으로 지켜본다(앱 전환·끊김은 워처가 재접속).
        watcherRef.current?.stop();
        watcherRef.current = watchUploadStatus({
          url: `/public-upload/@${encodeURIComponent(handle || '')}/upload-status`,
          token,
          onState: setWatch,
        });
      } else {
        setUploadError(json?.error || '올리지 못했어요.');
      }
    }).catch(() => {
      setUploading(false);
      setProcessing(false);
      setUploadError('올리는 중 문제가 생겼어요.');
    });
  };

  const bg = profile?.profileBackground;
  const displayName = profile?.name || (handle ? `@${handle}` : '');

  if (phase === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">불러오는 중…</div>;
  }
  if (phase === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-700 mb-2">존재하지 않는 페이지입니다</p>
          <p className="text-gray-400">주소를 다시 확인해 주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 공개 첨부 blob 다운로드용 숨은 앵커(토큰 헤더 전송 → URL 노출 금지). */}
      <a ref={dlAnchorRef} className="hidden" aria-hidden="true" />
      {/* 상단 배경 */}
      <div
        className="h-80 md:h-[28rem] bg-gray-200 bg-center bg-cover flex items-end"
        style={bg ? { backgroundImage: `url(${bg})` } : undefined}
      >
        <div className="w-full bg-gradient-to-t from-black/50 to-transparent p-4 md:p-6">
          <h1 className="text-white text-xl md:text-3xl font-bold drop-shadow">{displayName}</h1>
        </div>
      </div>

      {/* 카드들을 뷰포트 폭에 따라 흐르게(masonry) — 좁으면 1열, 넓으면 2~3열. 각 카드는 break-inside-avoid. */}
      <div className="max-w-lg lg:max-w-4xl xl:max-w-6xl mx-auto px-4 py-6 lg:columns-2 xl:columns-3 gap-4">
        {/* owner 안내문(마크다운). dangerouslySetInnerHTML 미사용 — renderMarkdown 이 React 노드 생성. */}
        {profile?.notice && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 break-inside-avoid text-sm text-gray-700 leading-relaxed">
            {renderMarkdown(profile.notice)}
          </div>
        )}
        {phase === 'code' && (
          <form onSubmit={handleVerify} className="bg-white border border-gray-200 rounded-lg p-6 mb-4 break-inside-avoid">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">영상 등록하기</h2>
            <p className="text-sm text-gray-500 mb-4">이 페이지에 영상을 올리려면 업로드 코드가 필요해요.</p>
            {/* name + autoComplete: 브라우저가 이전 입력값을 저장하고 재방문 시 자동완성/드롭다운 제안.
                코드는 autoComplete="on"(일반 폼 히스토리 — SMS OTP 취급 방지 위해 one-time-code 는 쓰지 않음). */}
            <input
              type="text"
              name="upload_code"
              autoComplete="on"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="업로드 코드"
              autoFocus
              className="w-full border border-gray-300 rounded px-3 py-2 mb-3 focus:border-blue-500 outline-none"
            />
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일 (선택)"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-1 focus:border-blue-500 outline-none"
            />
            <p className="text-xs text-gray-400 mb-3">이메일을 입력하면 승인 여부를 알려드립니다.</p>
            {codeError && <p className="text-sm text-red-500 mb-2">{codeError}</p>}
            <button
              type="submit"
              disabled={verifying || !code}
              className="w-full bg-blue-500 text-white py-2 rounded font-medium disabled:opacity-50 hover:bg-blue-600 transition-colors"
            >
              {verifying ? '확인 중…' : '코드 확인'}
            </button>
          </form>
        )}

        {phase === 'upload' && (
          <form ref={formRef} onSubmit={handleUpload} className="bg-white border border-gray-200 rounded-lg p-6 mb-4 break-inside-avoid">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">영상 업로드</h2>

            <label className="block text-sm font-medium text-gray-700 mb-1">영상·사진</label>
            {/* 모바일: 촬영 버튼은 카메라를 바로 연다(영상=video capture, 사진=image capture). 선택=보관함/파일. */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              <button type="button" onClick={() => pickFile('video/*', true)} disabled={uploading}
                className="border border-gray-300 rounded py-2 text-sm hover:bg-gray-50 disabled:opacity-50">📹 영상 촬영</button>
              <button type="button" onClick={() => pickFile('image/*', true)} disabled={uploading}
                className="border border-gray-300 rounded py-2 text-sm hover:bg-gray-50 disabled:opacity-50">📷 사진 촬영</button>
              <button type="button" onClick={() => pickFile('video/*,image/*', false)} disabled={uploading}
                className="border border-gray-300 rounded py-2 text-sm hover:bg-gray-50 disabled:opacity-50">📁 파일 선택</button>
            </div>
            {/* 실제 파일 입력(숨김) — FormData 는 name="file" 로 이 input 에서 읽는다. required 대신 handleUpload JS 검증. */}
            <input
              type="file"
              name="file"
              ref={fileInputRef}
              accept="video/*,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setSelectedFileName(f?.name || '');
                setSelectedIsImage(!!f?.type.startsWith('image/'));
                // 파일명 날짜("20260909"·"20260827-1900") → 행사 시작 프리필(연동 핸들러 경유).
                const fnDate = f ? eventDateFromFilename(f.name) : null;
                if (fnDate) applyEventStart(fnDate);
              }}
              className="hidden"
            />
            {selectedFileName && <p className="text-sm text-gray-700 mb-3 truncate">선택됨: {selectedFileName}</p>}

            {selectedIsImage && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">길이(초)</label>
                  <input name="image_duration_secs" type="number" min="1" step="1" defaultValue="10"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">너비(px)</label>
                  <input name="image_width" type="number" min="1" step="1" defaultValue="1920"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </div>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">제목(선택)</label>
            <input name="title" type="text" placeholder="영상 제목"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-3 text-sm focus:border-blue-500 outline-none" />

            <label className="block text-sm font-medium text-gray-700 mb-1">행사 시작·종료 일시(선택)</label>
            {/* 시작·종료 모두 datetime-local 로 통일. 위 달력을 클릭하면 시작의 날짜가 채워진다(시각은 유지). */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 w-8">시작</span>
              <input type="datetime-local" aria-label="시작 일시"
                value={eventStart}
                onChange={(e) => applyEventStart(e.target.value)}
                onBlur={onBlurEventStart}
                className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2 focus:border-blue-500 outline-none text-sm" />
            </div>
            {/*
              행사 시간 — **저장되지 않는 입력 보조**. 시작·종료와 양방향으로 물린다.
              "몇 시에 끝나지?"를 사용자가 암산하지 않게 하는 것이 목적이다.
            */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 w-8">시간</span>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                {/* step=1 — step=5 는 min=1 이 기준점이라 60·120 같은 5의 배수를 stepMismatch 로 거절했다 */}
                <input type="number" min="1" step="1" inputMode="numeric" aria-label="행사 시간(분)"
                  value={durationMin}
                  onChange={(e) => onChangeDuration(e.target.value)}
                  disabled={!eventStart}
                  title={eventStart ? '' : '행사 시작 시각을 먼저 입력하세요'}
                  placeholder="60"
                  className="w-24 border border-gray-300 rounded px-3 py-2 focus:border-blue-500 outline-none text-sm disabled:bg-gray-100" />
                <span className="text-xs text-gray-500">분</span>
                {humanizeMinutes(Number(durationMin)) && (
                  <span className="text-xs text-gray-400">({humanizeMinutes(Number(durationMin))})</span>
                )}
              </div>
            </div>
            {/* 종료(만료)는 시작과 다른 날 가능. 시작 선택 시 시작+행사시간(기본 1시간). */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 w-8">종료</span>
              <input type="datetime-local" aria-label="종료 일시(만료)"
                value={eventEnd}
                onChange={(e) => onChangeEventEnd(e.target.value)}
                className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2 focus:border-blue-500 outline-none text-sm" />
            </div>
            {durationInvalid && (
              <p className="text-xs text-red-500 mb-1">종료가 시작보다 이릅니다 — 시각을 확인해 주세요.</p>
            )}
            {(() => {
              const cd = countdownPreview(eventStart.slice(0, 10));
              return cd ? <p className={`text-sm font-semibold mb-1 ${cd.past ? 'text-red-500' : 'text-blue-600'}`}>{cd.text}</p> : null;
            })()}
            <p className="text-xs text-gray-400 mb-3">시작 시각은 카운트다운 표시에, 종료 시각은 영상이 내려가는 시점(다음 행사 영상으로 전환)에 쓰입니다.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">표시 문구(선택)</label>
            {/*
              "장소: {이름}" 기본값·지우지 말라는 안내는 제거(2026-09-17) — 타 운영처
              화면의 출처 표시는 이제 슬롯 라벨("다른 장소 소식 - {이름}")이 담당한다.
              이 칸은 미디어에 빠진 정보(날짜·팀명)를 보충하는 자유 문구다.
            */}
            <textarea
              name="event_label"
              rows={2}
              placeholder="예: 9월 27일 (토) 저녁 7시"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 outline-none resize-y"
            />
            <p className="text-xs text-gray-500 mb-3 mt-1">
              업로드한 이미지나 영상에 날짜나 팀명이 없으면, 여기에 입력하세요
            </p>

            {/* 오버레이 표시 위치 — 비로그인 업로더가 지정. 1=현재 스타일, 2~5=코너. */}
            <label className="block text-sm font-medium text-gray-700 mb-1">표시 위치</label>
            <p className="text-xs text-gray-400 mb-2">행사일까지 남은 날짜와 표시 문구에 입력한 글이 지정 위치에 나옵니다.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((pos) => (
                <button
                  key={pos}
                  type="button"
                  onClick={() => setOverlayPos(pos)}
                  title={OVERLAY_POS_LABELS[pos]}
                  className={`overflow-hidden rounded border-2 transition-colors ${overlayPos === pos ? 'border-blue-500' : 'border-gray-200 hover:border-gray-400'}`}
                >
                  <img src={`/static/overlay-pos/pos${pos}.png`} alt={OVERLAY_POS_LABELS[pos]} className="block w-[64px] h-auto" />
                  <span className={`block py-0.5 text-center text-xs ${overlayPos === pos ? 'bg-blue-500 text-white' : 'text-gray-600'}`}>{OVERLAY_POS_LABELS[pos]}</span>
                </button>
              ))}
            </div>

            {uploading && (
              <div className="mb-3">
                <div className="h-2 bg-gray-200 rounded overflow-hidden">
                  <div className={`h-full bg-blue-500 transition-all ${processing ? 'animate-pulse' : ''}`} style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {processing ? '올리기 끝 · 확인하고 있어요 (몇 초 걸려요)' : `올리는 중… ${Math.round(progress)}%`}
                </p>
              </div>
            )}
            {uploadError && <p className="text-sm text-red-500 mb-2">{uploadError}</p>}

            <button
              type="submit"
              disabled={uploading || durationInvalid}
              className="w-full bg-blue-500 text-white py-2 rounded font-medium disabled:opacity-50 hover:bg-blue-600 transition-colors"
            >
              {processing ? '확인 중…' : uploading ? '올리는 중…' : '등록 요청'}
            </button>
          </form>
        )}

        {/* 자료 보내기 — 영상과 무관하게 운영처로 문서·파일 전송(코드 입력 후). 공개/비공개 선택. */}
        {phase === 'upload' && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 break-inside-avoid">
            <p className="text-sm font-semibold text-gray-800 mb-1">추가 자료 첨부 (선택)</p>
            <p className="text-xs text-gray-500 mb-3">영상이 아니어도 운영처에 문서·파일을 보낼 수 있어요. (PDF·한글·이미지·영상·zip 등, 파일당 50MB)</p>
            <label className="flex items-center gap-2 text-sm text-gray-600 mb-1 cursor-pointer">
              <input type="checkbox" checked={acctIsPublic} onChange={(e) => setAcctIsPublic(e.target.checked)} />
              공개 (이 페이지에서 내려받을 수 있어요) — 끄면 운영처만 볼 수 있어요
            </label>
            <p className="text-xs text-amber-600 mb-3">운영자와 협의가 없을 경우, 공개 못할 중요한 자료는 올리지 마세요.</p>
            <input ref={acctInputRef} type="file" multiple disabled={acctUploading}
              onChange={(e) => setAcctFiles(e.target.files ? Array.from(e.target.files) : [])}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-300 file:bg-gray-50 file:text-sm" />
            {acctFiles.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 truncate">{acctFiles.map((f) => f.name).join(', ')}</p>
                <button type="button" onClick={() => uploadAccountMaterials()} disabled={acctUploading}
                  className="mt-2 w-full bg-blue-500 text-white py-2 rounded font-medium text-sm disabled:opacity-50 hover:bg-blue-600 transition-colors">
                  {acctUploading ? '보내는 중…' : `올리기 (${acctFiles.length}개)`}
                </button>
              </div>
            )}
            {attachErr && <p className="text-xs text-red-500 mt-2">{attachErr}</p>}
            {myAccount.length > 0 && (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded mt-3">
                {myAccount.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${a.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{a.isPublic ? '공개' : '비공개'}</span>
                    <span className="flex-1 truncate text-gray-700" title={a.filename}>{a.filename}</span>
                    <span className="text-xs text-gray-400 shrink-0">{fmtBytes(a.byteSize)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {phase === 'done' && (() => {
          // 올린 뒤 서버 쪽 게재 준비(사진이면 1/2·2/2, 영상이면 N%)를 그대로 보여준다.
          const kind = watch?.kind ?? 'waiting';
          const icon = kind === 'done' ? '✅' : kind === 'failed' ? '⚠️' : kind === 'detached' ? '📨' : '⏳';
          const sub = kind === 'done' ? '관리자가 확인하면 화면에 나와요.'
            : kind === 'failed' ? (watch?.message || '다른 파일로 다시 올려 주세요.')
            : kind === 'detached' ? '이 화면을 닫아도 준비는 계속돼요.'
            : '이 화면을 닫아도 준비는 계속돼요. 끝나면 관리자가 확인한 뒤 화면에 나와요.';
          return (
            <>
            <div className="bg-white border border-gray-200 rounded-lg p-8 mb-4 break-inside-avoid text-center">
              <div className="text-4xl mb-3">{icon}</div>
              <p className="text-lg font-semibold text-gray-800 mb-1">{watch?.text ?? '올렸어요 · 게재 준비 중'}</p>
              {kind === 'working' && watch?.percent != null && (
                <div className="h-2 bg-gray-200 rounded overflow-hidden max-w-xs mx-auto mb-2">
                  <div className="h-full bg-blue-500 transition-all" style={{ width: `${watch.percent}%` }} />
                </div>
              )}
              {(kind === 'waiting' || (kind === 'working' && watch?.percent == null)) && (
                <div className="h-2 bg-gray-200 rounded overflow-hidden max-w-xs mx-auto mb-2">
                  <div className="h-full w-1/3 bg-blue-400 animate-pulse" />
                </div>
              )}
              <p className={`text-sm mb-4 ${kind === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>{sub}</p>
              <button onClick={handleUploadMore} className="text-blue-500 text-sm hover:underline">
                {kind === 'failed' ? '다시 올리기' : '영상 더 올리기'}
              </button>
            </div>

            {/* 방금 올린 영상에 문서/파일 첨부(재생과 무관). 공개/비공개 선택. 전송 실패가 아닐 때만 노출. */}
            {kind !== 'failed' && kind !== 'detached' && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 break-inside-avoid">
                <p className="text-sm font-semibold text-gray-800 mb-1">추가 자료 첨부 (선택)</p>
                <p className="text-xs text-gray-500 mb-3">이 영상과 관련된 문서·파일을 올릴 수 있어요. (PDF·한글·이미지·영상·zip 등, 파일당 50MB)</p>
                <label className="flex items-center gap-2 text-sm text-gray-600 mb-1 cursor-pointer">
                  <input type="checkbox" checked={attachIsPublic} onChange={(e) => setAttachIsPublic(e.target.checked)} />
                  공개 (이 페이지에서 누구나 내려받을 수 있어요) — 끄면 운영처만 볼 수 있어요
                </label>
                <p className="text-xs text-amber-600 mb-3">운영자와 협의가 없을 경우, 공개 못할 중요한 자료는 올리지 마세요.</p>
                <input ref={attachInputRef} type="file" multiple disabled={attachUploading}
                  onChange={(e) => setAttachFiles(e.target.files ? Array.from(e.target.files) : [])}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-300 file:bg-gray-50 file:text-sm" />
                {attachFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 truncate">{attachFiles.map((f) => f.name).join(', ')}</p>
                    <button type="button" onClick={() => uploadAttachments()} disabled={attachUploading}
                      className="mt-2 w-full bg-blue-500 text-white py-2 rounded font-medium text-sm disabled:opacity-50 hover:bg-blue-600 transition-colors">
                      {attachUploading ? '올리는 중…' : `올리기 (${attachFiles.length}개)`}
                    </button>
                  </div>
                )}
                {attachErr && <p className="text-xs text-red-500 mt-2">{attachErr}</p>}
                {myAttachments.length > 0 && (
                  <ul className="divide-y divide-gray-100 border border-gray-100 rounded mt-3">
                    {myAttachments.map((a) => (
                      <li key={a.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${a.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{a.isPublic ? '공개' : '비공개'}</span>
                        <span className="flex-1 truncate text-gray-700" title={a.filename}>{a.filename}</span>
                        <span className="text-xs text-gray-400 shrink-0">{fmtBytes(a.byteSize)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            </>
          );
        })()}

        {/* 공개 자료 — 코드 입력 후, 이 운영처의 공개 첨부를 영상 제목별로 + 운영처 자료(영상 무관). */}
        {(phase === 'upload' || phase === 'done') && (pubGroups.length > 0 || pubAccount.length > 0) && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 break-inside-avoid">
            <p className="text-sm font-semibold text-gray-800 mb-3">공개 자료</p>
            <div className="space-y-3">
              {pubAccount.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">운영처 자료</p>
                  <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
                    {pubAccount.map((a) => renderPubItem(a))}
                  </ul>
                </div>
              )}
              {pubGroups.map((g) => (
                <div key={g.fileHash}>
                  <p className="text-xs font-medium text-gray-600 mb-1 truncate" title={g.videoTitle}>{g.videoTitle}</p>
                  <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
                    {g.attachments.map((a) => renderPubItem(a))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 행사 현황 — 맨 마지막 카드로 노출. */}
        {phase === 'upload' && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 break-inside-avoid">
            <UploadCalendar
              events={calendarEvents}
              handle={handle}
              onSelectDate={(k) => {
                // 달력 클릭도 직접 입력과 **같은 규칙**을 탄다 — 날짜만 채우고(시각 유지)
                // 행사 시간을 유지한 채 종료가 따라 움직인다.
                applyEventStart(withDatePart(eventStart, k));
                formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicUploadPage;
